import React, { useState, useRef } from 'react';
import { Heart, X, RotateCcw } from 'lucide-react';

// ============================================
// BrowseMode — Carousel discovery
// ============================================
// All profiles shown as cards in a horizontal grid.
// Swipe horizontally to browse. Tap a card to inspect.
// Three actions: Like, Pass, Maybe.
// ============================================

export default function BrowseMode({ profiles, currentIndex, user, onSwipe, onMaybe, onInspect }) {
  var [inspecting, setInspecting] = useState(null);

  if (!profiles || profiles.length === 0) {
    return (
      <div style={{ textAlign: 'center', padding: '60px 20px', color: 'var(--text-muted)' }}>
        <span style={{ fontSize: 48, display: 'block', marginBottom: 12 }}>🔍</span>
        <p style={{ fontSize: 15, fontWeight: 600 }}>No profiles to browse right now</p>
        <p style={{ fontSize: 13 }}>Check back soon — new people join every day</p>
      </div>
    );
  }

  // If inspecting a profile, show the detail view
  if (inspecting !== null) {
    var p = profiles[inspecting];
    if (!p) { setInspecting(null); return null; }
    return (
      <div style={{ padding: '0 16px 100px' }}>
        <button onClick={() => setInspecting(null)} style={{
          background: 'none', border: 'none', color: 'var(--primary)', fontSize: 14,
          fontWeight: 600, cursor: 'pointer', padding: '12px 0',
        }}>← Back to browse</button>

        {/* Full profile view */}
        <div style={{ borderRadius: 16, overflow: 'hidden', marginBottom: 16 }}>
          {p.photos && p.photos.length > 0 && (
            <img src={p.photos[0].url} alt={p.firstName} style={{
              width: '100%', height: 400, objectFit: 'cover', borderRadius: 16,
            }} />
          )}
        </div>

        <div style={{ padding: '0 4px' }}>
          <h2 style={{ fontSize: 24, fontWeight: 700, color: 'var(--text-primary)', margin: '0 0 4px' }}>
            {p.firstName}{p.age ? ', ' + p.age : ''}
          </h2>
          {p.location?.city && (
            <p style={{ fontSize: 13, color: 'var(--text-secondary)', margin: '0 0 12px' }}>
              📍 {p.location.city}
            </p>
          )}
          {p.bio && (
            <p style={{ fontSize: 14, color: 'var(--text-primary)', lineHeight: 1.7, margin: '0 0 16px' }}>
              {p.bio}
            </p>
          )}

          {/* Interests */}
          {p.interests && p.interests.length > 0 && (
            <div style={{ marginBottom: 16 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-secondary)', marginBottom: 8 }}>Interests</div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {p.interests.map(function(int, i) {
                  return (
                    <span key={i} style={{
                      padding: '5px 12px', borderRadius: 16, fontSize: 12, fontWeight: 500,
                      background: 'var(--surface-alt)', color: 'var(--text-primary)',
                    }}>{int}</span>
                  );
                })}
              </div>
            </div>
          )}

          {/* More photos */}
          {p.photos && p.photos.length > 1 && (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 16 }}>
              {p.photos.slice(1).map(function(photo, i) {
                return (
                  <img key={i} src={photo.url} alt="" style={{
                    width: '100%', height: 180, objectFit: 'cover', borderRadius: 12,
                  }} />
                );
              })}
            </div>
          )}

          {/* Occupation / Education */}
          {(p.occupation || p.education) && (
            <div style={{ marginBottom: 16, fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.8 }}>
              {p.occupation && <div>💼 {p.occupation}</div>}
              {p.education && <div>🎓 {p.education}</div>}
              {p.height && <div>📏 {p.height}cm</div>}
            </div>
          )}
        </div>

        {/* Action buttons */}
        <div style={{ display: 'flex', justifyContent: 'center', gap: 16, padding: '16px 0' }}>
          <button onClick={() => { onSwipe('left', inspecting); setInspecting(null); }} style={{
            width: 60, height: 60, borderRadius: '50%', border: '2px solid #ff6b6b',
            background: 'var(--surface)', color: '#ff6b6b', cursor: 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}><X size={28} strokeWidth={3} /></button>

          <button onClick={() => { onMaybe(inspecting); setInspecting(null); }} style={{
            width: 52, height: 52, borderRadius: '50%', border: '2px solid #fbbf24',
            background: 'var(--surface)', color: '#fbbf24', cursor: 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}><RotateCcw size={22} strokeWidth={2.5} /></button>

          <button onClick={() => { onSwipe('right', inspecting); setInspecting(null); }} style={{
            width: 60, height: 60, borderRadius: '50%', border: '2px solid #51cf66',
            background: 'var(--surface)', color: '#51cf66', cursor: 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}><Heart size={28} strokeWidth={3} /></button>
        </div>
      </div>
    );
  }

  // ── Carousel grid view ─────────────────────────────────────
  return (
    <div style={{ padding: '0 12px 100px' }} role="feed" aria-label="Browse profiles">
      <div style={{
        display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
        gap: 10,
      }}>
        {profiles.map(function(p, i) {
          return (
            <div key={p.userId || i} onClick={() => setInspecting(i)} style={{
              borderRadius: 14, overflow: 'hidden', cursor: 'pointer',
              background: 'var(--surface)', border: '1px solid var(--border)',
              transition: 'transform 0.15s',
            }} role="article" aria-label={(p.firstName || 'Profile') + (p.age ? ', age ' + p.age : '') + (p.location?.city ? ', ' + p.location.city : '')}>
              {/* Photo */}
              <div style={{ position: 'relative', height: 200 }}>
                {p.photos && p.photos[0] ? (
                  <img src={p.photos[0].url} alt={p.firstName} style={{
                    width: '100%', height: '100%', objectFit: 'cover',
                  }} />
                ) : (
                  <div style={{
                    width: '100%', height: '100%', background: 'var(--surface-alt)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: 40, color: 'var(--text-muted)',
                  }}>👤</div>
                )}
                {/* Gradient overlay */}
                <div style={{
                  position: 'absolute', bottom: 0, left: 0, right: 0, height: 60,
                  background: 'linear-gradient(transparent, rgba(0,0,0,0.6))',
                }} />
                <div style={{
                  position: 'absolute', bottom: 8, left: 10, right: 10,
                  color: '#fff', fontSize: 14, fontWeight: 600,
                }}>
                  {p.firstName}{p.age ? ', ' + p.age : ''}
                </div>
              </div>

              {/* Mini info */}
              <div style={{ padding: '8px 10px' }}>
                {p.location?.city && (
                  <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginBottom: 4 }}>
                    📍 {p.location.city}
                  </div>
                )}
                {p.interests && p.interests.length > 0 && (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                    {p.interests.slice(0, 3).map(function(int, j) {
                      return (
                        <span key={j} style={{
                          fontSize: 10, padding: '2px 8px', borderRadius: 10,
                          background: 'var(--surface-alt)', color: 'var(--text-secondary)',
                        }}>{int}</span>
                      );
                    })}
                    {p.interests.length > 3 && (
                      <span style={{ fontSize: 10, color: 'var(--text-muted)' }}>+{p.interests.length - 3}</span>
                    )}
                  </div>
                )}
              </div>

              {/* Quick action buttons on the card */}
              <div style={{ display: 'flex', borderTop: '1px solid var(--border)' }} role="group" aria-label="Actions">
                <button onClick={(e) => { e.stopPropagation(); onSwipe('left', i); }} style={{
                  flex: 1, padding: '10px', border: 'none', background: 'transparent',
                  color: '#ff6b6b', fontSize: 18, cursor: 'pointer',
                }} aria-label={'Pass on ' + (p.firstName || 'this profile')}>✕</button>
                <button onClick={(e) => { e.stopPropagation(); onMaybe(i); }} style={{
                  flex: 1, padding: '10px', border: 'none', background: 'transparent',
                  color: '#fbbf24', fontSize: 16, cursor: 'pointer',
                  borderLeft: '1px solid var(--border)', borderRight: '1px solid var(--border)',
                }} aria-label={'Maybe on ' + (p.firstName || 'this profile')}>↩</button>
                <button onClick={(e) => { e.stopPropagation(); onSwipe('right', i); }} style={{
                  flex: 1, padding: '10px', border: 'none', background: 'transparent',
                  color: '#51cf66', fontSize: 18, cursor: 'pointer',
                }} aria-label={'Like ' + (p.firstName || 'this profile')}>♡</button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
